import React, { memo, useEffect, useState, type FC } from 'react';
import { TouchableOpacity, View, type ViewStyle } from 'react-native';

import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';

export type SwitchProps = {
  /** On/off state. Controlled when provided; otherwise the switch tracks its own state. */
  value?: boolean;
  onValueChange?: (value: boolean) => void;
  disabled?: boolean;
  style?: ViewStyle | ViewStyle[];
};

/** A pill toggle switch: blue track + white thumb when on, grey when off. Controlled
 *  (`value` + `onValueChange`) or uncontrolled. Mirrors the platform switch look from Figma. */
export const Switch: FC<SwitchProps> = memo(({ value, onValueChange, disabled = false, style }) => {
  const [internal, setInternal] = useState<boolean>(value ?? false);
  const isControlled = value !== undefined;
  const isOn = isControlled ? value : internal;
  const styles = useStyles();

  useEffect(() => {
    if (!isControlled) return;
    setInternal(value ?? false);
  }, [value, isControlled]);

  const handlePress = () => {
    if (disabled) return;
    const next = !isOn;
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={disabled}
      activeOpacity={0.8}
      accessibilityRole="switch"
      accessibilityState={{ checked: isOn, disabled }}
      style={[
        styles.track,
        isOn ? styles.trackOn : styles.trackOff,
        disabled && styles.disabled,
        style,
      ]}>
      <View style={[styles.thumb, isOn ? styles.thumbOn : styles.thumbOff]} />
    </TouchableOpacity>
  );
});

Switch.displayName = 'Switch';

const useStyles = createStyles(({ scale }) => ({
  track: {
    width: scale(40),
    height: scale(24),
    borderRadius: scale(12),
    padding: scale(2),
    justifyContent: 'center',
  },
  trackOn: {
    backgroundColor: colors.Blue,
  },
  trackOff: {
    backgroundColor: colors['3'],
  },
  disabled: {
    opacity: 0.6,
  },
  thumb: {
    width: scale(20),
    height: scale(20),
    borderRadius: scale(10),
    backgroundColor: colors['0'],
  },
  thumbOn: {
    alignSelf: 'flex-end',
  },
  thumbOff: {
    alignSelf: 'flex-start',
  },
}));
